import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Image } from 'foundations-components/transitional/components/Image';
import styledWithTheme from 'foundations-theming/utils';
import { mobileScreen, trellisBreakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { useMedia } from 'pricing-pages-ui-components/hooks/useMedia';
import { useHubSpotCreditsInventoryInfo } from '../../hooks/inventory/useHubSpotCreditsInventoryInfo';
import { SingleSkeletonLoader } from '../SingleSkeletonLoader';
import { CustomError } from '../error-boundary/CustomErrors';
import { getHubSpotCreditsHeroImage } from '../../../components/key-features/getLocalizedImages';
import { withErrorBoundary } from '../error-boundary/withErrorBoundary';
import { BuyAdditionalCreditsCard } from './BuyAdditionalCreditsCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HeroDescription = ({
  isLoading,
  ownsAdditionalCredits
}) => {
  if (isLoading) {
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 48,
      width: "100%"
    });
  }
  return /*#__PURE__*/_jsx(Text, {
    typography: "body-100",
    children: ownsAdditionalCredits ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "pricingPage.pages.hubspot-credits.hero.description.ownsAdditional"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "pricingPage.pages.hubspot-credits.hero.description.default"
    })
  });
};
const HeroSection = styledWithTheme.section(({
  theme
}) => {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.space[800],
    paddingBlock: theme.space[1300],
    [trellisBreakpoint[mobileScreen]]: {
      paddingInline: theme.space[300],
      paddingBlockStart: theme.space[0],
      paddingBlockEnd: theme.space[800]
    }
  };
});
const HubspotCreditsHeroBase = () => {
  const isSmallerThanCs = useMedia('cs');
  const {
    ownedHubSpotCreditsTotal,
    loading: hubSpotCreditsInventoryInfoLoading,
    error: hubSpotCreditsInventoryInfoError
  } = useHubSpotCreditsInventoryInfo();
  const ownsAdditionalCredits = ownedHubSpotCreditsTotal > 0;
  if (hubSpotCreditsInventoryInfoError) {
    throw new CustomError('Failed getting hubspot credits inventory info', {
      error: hubSpotCreditsInventoryInfoError
    });
  }
  if (hubSpotCreditsInventoryInfoLoading) {
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 48,
      width: "100%"
    });
  }
  return /*#__PURE__*/_jsxs(HeroSection, {
    children: [/*#__PURE__*/_jsx(View, {
      as: "header",
      flexBasis: "100%",
      flex: 1,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "600",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h1",
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.products.tabs.hubspot-credits.pageTitle"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "heading-800",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.hubspot-credits.hero.headline"
          })
        }), /*#__PURE__*/_jsx(HeroDescription, {
          isLoading: hubSpotCreditsInventoryInfoLoading,
          ownsAdditionalCredits: ownsAdditionalCredits
        })]
      })
    }), !isSmallerThanCs && /*#__PURE__*/_jsx(Flex, {
      flexBasis: "100%",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      children: hubSpotCreditsInventoryInfoLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      }) : ownsAdditionalCredits ? /*#__PURE__*/_jsx(BuyAdditionalCreditsCard, {}) : /*#__PURE__*/_jsx(Image, {
        "data-test-id": "hubspot-credits-hero-image",
        src: getHubSpotCreditsHeroImage(),
        alt: I18n.text('pricingPage.pages.hubspot-credits.hero.alt')
      })
    })]
  });
};
export const HubspotCreditsHero = withErrorBoundary(HubspotCreditsHeroBase, {
  fallback: null,
  errorBoundaryName: 'HubSpotCreditsHero'
});